import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { ProcurementSpendTrend } from '@/types/domain/dashboard';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Activity, TrendingUp, PieChart, ShieldCheck, Zap } from 'lucide-react';
import { SourceBadge } from '@/components/data-display/SourceBadge';

interface ProcurementSpendChartProps {
  data: ProcurementSpendTrend[];
  totalSpend: string;
  trendPercentage: string;
}

export const ProcurementSpendChart: React.FC<ProcurementSpendChartProps> = ({
  data,
  totalSpend,
  trendPercentage,
}) => {
  // Category Spend Breakdown Data for Enterprise Bottom Intelligence Panel
  const categoryBreakdown = [
    { name: 'IT Hardware & Workstations', amount: '₹4.20 Cr', percent: 45, colorClass: 'bg-[#3d5ad6]' },
    { name: 'Cloud Infrastructure & Azure', amount: '₹2.80 Cr', percent: 30, colorClass: 'bg-[#4767f0]' },
    { name: 'Facilities & Maintenance', amount: '₹1.50 Cr', percent: 16, colorClass: 'bg-[#798fec]' },
    { name: 'Professional Services', amount: '₹0.90 Cr', percent: 9, colorClass: 'bg-[#04050b] dark:bg-slate-400' },
  ];

  return (
    <Card className="border-border rounded-2xl shadow-2xs h-full flex flex-col justify-between font-heading overflow-hidden relative">
      {/* Top Header */}
      <CardHeader className="pb-4 border-b border-border/60">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="font-heading text-lg font-extrabold text-foreground flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Procurement Overview & Spend Trajectory
              </CardTitle>
              <SourceBadge source="PROCUREAI" showIcon={false} className="text-[9px] font-bold" />
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5">
              Monthly Purchase Order value & committed spend baseline trajectory
            </p>
          </div>

          <div className="flex items-baseline gap-3">
            <span className="font-heading text-2xl font-extrabold text-foreground">{totalSpend}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 px-2.5 py-0.5 text-xs font-bold">
              <TrendingUp className="h-3.5 w-3.5" /> {trendPercentage}
            </span>
          </div>
        </div>
      </CardHeader>

      {/* Main Chart Canvas Area */}
      <CardContent className="pt-6 pb-2 px-4 h-64 sm:h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'currentColor' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'currentColor' }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}Cr`} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="rounded-xl border border-border bg-surface dark:bg-[#0e101f] p-3 shadow-xl backdrop-blur-md text-xs space-y-1 z-50">
                      <p className="font-bold text-foreground">{label} Procurement Spend</p>
                      <p className="text-primary font-extrabold font-mono text-sm">
                        Committed: {payload[0]?.payload.formattedSpend}
                      </p>
                      <p className="text-foreground-muted text-[10px]">Budget Baseline: ₹{payload[1]?.value} Cr</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ paddingTop: '8px', fontSize: '12px' }} />
            <Bar name="Committed PO Spend (Cr)" dataKey="spend" fill="#3d5ad6" radius={[8, 8, 0, 0]} />
            <Bar name="Quarterly Target (Cr)" dataKey="target" fill="#798fec" radius={[8, 8, 0, 0]} opacity={0.6} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>

      {/* Bottom Category Spend & Live Performance Intelligence Panel (Fills vertical gap perfectly) */}
      <div className="border-t border-border/70 bg-muted/30 p-4 space-y-3 mt-auto">
        <div className="flex items-center justify-between">
          <span className="text-xs font-extrabold text-foreground uppercase tracking-wider flex items-center gap-1.5">
            <PieChart className="h-3.5 w-3.5 text-primary" />
            Top Category Spend Distribution
          </span>
          <span className="text-[11px] font-bold text-primary flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" /> Budget Utilization 84.5%
          </span>
        </div>

        {/* Category Spend Multi-Progress Bar */}
        <div className="space-y-1.5">
          <div className="h-2.5 w-full rounded-full bg-border overflow-hidden flex">
            {categoryBreakdown.map((cat, idx) => (
              <div
                key={idx}
                style={{ width: `${cat.percent}%` }}
                className={`h-full ${cat.colorClass} transition-all duration-500`}
                title={`${cat.name}: ${cat.amount} (${cat.percent}%)`}
              />
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
            {categoryBreakdown.map((cat, idx) => (
              <div key={idx} className="flex flex-col text-[11px]">
                <div className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${cat.colorClass} shrink-0`} />
                  <span className="font-bold text-foreground truncate">{cat.name}</span>
                </div>
                <div className="flex items-center justify-between text-foreground-secondary pl-3 text-[10px]">
                  <span className="font-mono font-semibold">{cat.amount}</span>
                  <span className="font-bold text-primary">{cat.percent}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Performance KPI Micro-Bar */}
        <div className="flex items-center justify-between border-t border-border/50 pt-2 text-[10px] text-foreground-muted font-semibold">
          <span className="flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> Avg PO Cycle: <strong>1.4 Days</strong>
          </span>
          <span>On-Time Vendor Delivery: <strong>98.2%</strong></span>
          <span className="hidden sm:inline">SAP BAPI Sync: <strong className="text-primary">100% Real-Time</strong></span>
        </div>
      </div>
    </Card>
  );
};
